/**
 * Shared constants and the cross-window channel for Patient Review Mode.
 *
 * The review page runs in a SEPARATE top-level window (`window.open`), so the
 * staff app and the review page share no React tree, no Redux store and no RTK
 * Query cache. Everything that has to cross that boundary goes through the
 * BroadcastChannel below.
 */

/**
 * Named window target. Reusing one name means a second click re-targets the
 * window the user already dragged onto their patient-facing monitor instead of
 * spawning a second one — which is the whole reason this ships as a window and
 * not a modal.
 */
export const PATIENT_REVIEW_WINDOW_NAME = "sunoPatientReview";

/**
 * `popup=yes` asks for a chrome-less window (no address bar, no tab strip), so
 * the patient sees the page and nothing that could navigate away from it. Size
 * is a starting point only — the window is the staff member's to move and
 * resize, and the layout is fluid.
 */
export const PATIENT_REVIEW_WINDOW_FEATURES =
  "popup=yes,noopener=no,width=1400,height=1000";

export const PATIENT_REVIEW_CHANNEL_NAME = "suno-patient-review";

export type PatientReviewChannelMessage =
  /** Staff logged out, or the staff tab went away. The review window closes itself. */
  | { type: "close" }
  /** Staff re-opened the review for this patient. Un-blank and reset the idle timer. */
  | { type: "wake"; patientId: number };

export const buildPatientReviewPath = (patientId: number | string): string =>
  `/patient-review/${patientId}`;

/**
 * BroadcastChannel is unavailable in jsdom and in older Safari, and constructing
 * one can throw in sandboxed contexts. Every caller here is a side effect on a
 * teardown or a click — none of them may be allowed to throw, so absence
 * degrades to "the message is not delivered", never to a crash.
 */
const withChannel = (run: (channel: BroadcastChannel) => void): void => {
  if (typeof BroadcastChannel === "undefined") return;
  let channel: BroadcastChannel | null = null;
  try {
    channel = new BroadcastChannel(PATIENT_REVIEW_CHANNEL_NAME);
    run(channel);
  } catch {
    // No-op: a review window that misses a message is recoverable, a thrown
    // logout is not.
  } finally {
    try {
      channel?.close();
    } catch {
      /* already torn down */
    }
  }
};

export const postPatientReviewMessage = (
  message: PatientReviewChannelMessage
): void => {
  withChannel((channel) => channel.postMessage(message));
};

/**
 * Called from `clearAuthStorage` in the auth slice, i.e. on every logout path.
 *
 * Load-bearing for PHI: when "keep me logged in" is unticked the session token
 * lives in sessionStorage, and sessionStorage is *copied* into a window opened
 * from this one. Clearing the staff tab's copy therefore does NOT sign the
 * review window out — it would keep rendering a patient's record on a
 * waiting-room monitor after the front desk logged off. This closes it.
 */
export const closePatientReviewWindows = (): void => {
  postPatientReviewMessage({ type: "close" });
};
